import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

// Color variables
const COLORS = {
  hotWater: "#E45C5E",
  hotWaterFlow: "#FE9E9F", // Lighter red for flow
  coldWater: "#55A9E1",
  coldWaterFlow: "#D8E3FC", // Lighter blue for flow
  dark: "#191C20",
  white: "#FFFFFF",
  blueShades: {
    darkest: "#326587",
    medium: "#55A9E1",
    light: "#D8E3FC",
    lighter: "#DFE1FC",
  },
  purpleShades: {
    blue: "#E4DFFA",
    medium: "#EADEF7",
    pink: "#EFDCF2",
    light: "#F3DBEE",
  },
  redShades: {
    darkest: "#752138",
    medium: "#E45C5E",
  },
  purpleShades2: {
    dark: "#5E2C5A",
  },
  controlPanel: "#08404D",
};

// Flow control variables
const FLOW = {
  // Animation timing (seconds)
  speed: {
    hot: 1, // Hot water flow speed
    cold: 1.2, // Cold water flow speed
  },
  // Particle configuration
  particles: {
    hot: {
      size: 2, // Dot size
      spacing: 20, // Space between dots
    },
    cold: {
      size: 2, // Dot size
      spacing: 20, // Space between dots
    },
  },
};

const pulseFlowHot = keyframes`
  0% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: -${FLOW.particles.hot.size + FLOW.particles.hot.spacing}; /* Matches exactly the combined value of dash and gap */
  }
`;

const pulseFlowCold = keyframes`
  0% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: ${FLOW.particles.cold.size + FLOW.particles.cold.spacing}; /* Matches exactly the combined value of dash and gap */
  }
`;

const waveAnimation = keyframes`
  0% {
    d: path("M545 115 Q570 112 595 115 T645 115 L645 155 L545 155 Z");
  }
  50% {
    d: path("M545 115 Q570 118 595 115 T645 115 L645 155 L545 155 Z");
  }
  100% {
    d: path("M545 115 Q570 112 595 115 T645 115 L645 155 L545 155 Z");
  }
`;

// Flow paths with sphere effect
const HotFlowPath = styled.path`
  stroke-dasharray: ${FLOW.particles.hot.size}, ${FLOW.particles.hot.spacing};
  animation: ${pulseFlowHot} ${FLOW.speed.hot}s linear infinite;
`;

const ColdFlowPath = styled.path`
  stroke-dasharray: ${FLOW.particles.cold.size}, ${FLOW.particles.cold.spacing};
  animation: ${pulseFlowCold} ${FLOW.speed.cold}s linear infinite;
`;

const WaterWave = styled.path`
  animation: ${waveAnimation} 3s ease-in-out infinite;
  fill: ${COLORS.coldWater};
  opacity: 0.7;
`;

export const HeatingDiagram = () => {
  return (
    <svg
      width="852"
      height="331"
      viewBox="0 0 852 331"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* LAYER 1: Base pipes and flow animation paths (bottom layer) */}

      {/* Water tank at top */}
      <path
        d="M652 69V155C652 158.866 648.866 162 645 162H546C542.134 162 539 158.866 539 155V69H652Z"
        stroke={COLORS.hotWater}
        strokeWidth="6"
      />

      {/* Water level in tank with wave animation (about 50% full) */}
      <WaterWave d="M545 115 Q570 112 595 115 T645 115 L645 155 L545 155 Z" />

      {/* Hot water flow (Red) - Static pipes */}
      <rect y="62.5039" width="409" height="12" fill={COLORS.hotWater} />

      {/* Cold water flow (Blue) - Static pipes */}
      <rect x="11.1367" y="254.77" width="397.854" height="12" fill={COLORS.coldWater} />
      <path
        d="M1.94439e-07 260.204L12 246.084L12 274.325L1.94439e-07 260.204Z"
        fill={COLORS.coldWater}
      />

      {/* Right curve sections - Cold */}
      <path
        d="M822.625 245.931C822.625 278.515 796.21 304.931 763.625 304.931H453.832V292.931H763.625C789.582 292.931 810.625 271.888 810.625 245.931V233.915H822.625V245.931Z"
        fill={COLORS.coldWater}
      />

      {/* Right curve sections - Hot */}
      <path
        d="M763.621 10.9844C796.206 10.9845 822.621 37.3997 822.621 69.9844V95.915H810.621V69.9844C810.621 44.0271 789.578 22.9845 763.621 22.9844H453.828V10.9844H763.621Z"
        fill={COLORS.hotWater}
      />

      {/* New middle section - Blue */}
      <path
        d="M739.452 205C737.133 235.463 711.681 259.458 680.624 259.458H431.035V247.458H680.624C705.049 247.458 725.123 228.827 727.407 205H739.452Z"
        fill={COLORS.coldWater}
      />

      {/* New top section - Hot */}
      <path
        d="M680.62 65.5054C711.693 65.5055 737.156 89.5271 739.451 120.013H727.407C725.146 96.1631 705.062 77.5055 680.62 77.5054H431.031V65.5054H680.62Z"
        fill={COLORS.hotWater}
      />

      {/* Internal flow animations for curved sections */}
      {/* Hot water curved sections - Combined into single continuous path */}
      <HotFlowPath
        d="M437.031 71.5054 H674.62 C705.693 71.5055 731.156 89.5271 733.451 114.013"
        stroke={COLORS.hotWaterFlow}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />

      {/* Cold water curved sections - Combined into single continuous path */}
      <ColdFlowPath
        d="M437.035 253.458 H674.624 C705.681 253.458 731.133 235.463 733.452 211"
        stroke={COLORS.coldWaterFlow}
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />

      {/* LAYER 3: Control panels and additional components (top layer) */}

      {/* Control panel 1 */}
      <path
        d="M733.219 139.343C733.219 138.045 732.174 137 730.875 137C729.576 137 728.531 138.045 728.531 139.343V141.394L718.375 140.124C716.646 140.124 715.25 141.521 715.25 143.249C715.25 144.977 716.646 146.373 718.375 146.373L728.531 145.104V149.498H726.705C725.25 149.498 723.863 150.074 722.838 151.099L721.314 152.622H711.344C710.045 152.622 709 153.667 709 154.965C709 156.264 710.045 157.309 711.344 157.309H722.281C722.906 157.309 723.502 157.064 723.941 156.625L726.148 154.418C726.295 154.272 726.49 154.194 726.705 154.194H735.836C736.041 154.194 736.246 154.272 736.393 154.418L738.59 156.625C739.029 157.064 739.625 157.309 740.25 157.309H744.156C749.762 157.309 754.312 161.858 754.312 167.463V169.025C754.312 169.455 753.961 169.806 753.531 169.806H748.844C748.414 169.806 748.062 169.455 748.062 169.025C748.062 166.008 745.611 163.557 742.594 163.557H739.439C738.639 163.557 737.896 163.967 737.467 164.641C736.07 166.809 733.639 168.244 730.875 168.244C728.111 168.244 725.68 166.809 724.293 164.641C723.863 163.967 723.121 163.557 722.32 163.557H711.344C710.045 163.557 709 164.602 709 165.901C709 167.199 710.045 168.244 711.344 168.244H721.119C723.404 171.095 726.93 172.93 730.875 172.93C734.82 172.93 738.346 171.095 740.631 168.244H742.594C743.023 168.244 743.375 168.595 743.375 169.025C743.375 172.042 745.826 174.493 748.844 174.493H753.531C756.549 174.493 759 172.042 759 169.025V167.463C759 159.271 752.35 152.622 744.156 152.622H741.217L739.693 151.099C738.668 150.074 737.281 149.498 735.826 149.498H733.219V145.104L743.375 146.373C745.104 146.373 746.5 144.977 746.5 143.249C746.5 141.521 745.104 140.124 743.375 140.124L733.219 141.394V139.343ZM750.094 178.34L748.316 182.479C748.141 182.88 748.053 183.319 748.053 183.758V183.876C748.053 185.604 749.449 187 751.178 187C752.906 187 754.303 185.604 754.303 183.876V183.758C754.303 183.319 754.215 182.889 754.039 182.479L752.262 178.34C752.076 177.9 751.646 177.617 751.168 177.617C750.689 177.617 750.27 177.9 750.074 178.34H750.094Z"
        fill={COLORS.controlPanel}
      />

      {/* Control panel 2 */}
      <path
        d="M792.833 120C789.561 120 786.917 122.681 786.917 126V198C786.917 201.319 789.561 204 792.833 204H804.667V192C804.667 185.381 809.973 180 816.5 180C823.027 180 828.333 185.381 828.333 192V204H840.167C843.439 204 846.083 201.319 846.083 198V126C846.083 122.681 843.439 120 840.167 120H792.833ZM822.417 192C822.417 188.681 819.773 186 816.5 186C813.227 186 810.583 188.681 810.583 192V204H822.417V192ZM804.667 210H792.833C786.307 210 781 204.619 781 198V126C781 119.381 786.307 114 792.833 114H840.167C846.693 114 852 119.381 852 126V198C852 204.619 846.693 210 840.167 210H828.333H822.417H810.583H804.667ZM792.833 136.5C792.833 134.006 794.812 132 797.271 132H806.146C808.605 132 810.583 134.006 810.583 136.5V145.5C810.583 147.994 808.605 150 806.146 150H797.271C794.812 150 792.833 147.994 792.833 145.5V136.5ZM798.75 138V144H804.667V138H798.75ZM826.854 132H835.729C838.188 132 840.167 134.006 840.167 136.5V145.5C840.167 147.994 838.188 150 835.729 150H826.854C824.395 150 822.417 147.994 822.417 145.5V136.5C822.417 134.006 824.395 132 826.854 132ZM828.333 144H834.25V138H828.333V144ZM792.833 160.5C792.833 158.006 794.812 156 797.271 156H806.146C808.605 156 810.583 158.006 810.583 160.5V169.5C810.583 171.994 808.605 174 806.146 174H797.271C794.812 174 792.833 171.994 792.833 169.5V160.5ZM798.75 162V168H804.667V162H798.75ZM826.854 156H835.729C838.188 156 840.167 158.006 840.167 160.5V169.5C840.167 171.994 838.188 174 835.729 174H826.854C824.395 174 822.417 171.994 822.417 169.5V160.5C822.417 158.006 824.395 156 826.854 156ZM828.333 168H834.25V162H828.333V168Z"
        fill={COLORS.controlPanel}
      />

      {/* Flow animation paths - Hot Water */}
      {/* Main horizontal hot water flow - Combined continuous path */}
      <HotFlowPath
        d="M12 68.5039 H302.328 C307 67.939 309 67.939 314 67.939 C319 67.939 322 67.939 324.066 67.9384 L332.219 68.5039 H409"
        stroke={COLORS.hotWaterFlow}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* Top section hot water flow - Combined continuous path */}
      <HotFlowPath
        d="M453.832 16.9844 H763.621 C789.578 16.9845 816.621 37.0271 816.621 69.9844 V95.915"
        stroke={COLORS.hotWaterFlow}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* Flow animation paths - Cold Water */}
      {/* Main horizontal cold water flow */}
      <ColdFlowPath
        d="M11.1367 260.77 H409"
        stroke={COLORS.coldWaterFlow}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* Bottom section cold water flow - Combined continuous path */}
      <ColdFlowPath
        d="M453.832 298.931 H763.625 C789.582 298.931 816.625 278.888 816.625 245.931 V233.915"
        stroke={COLORS.coldWaterFlow}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* LAYER 4: Heat exchanger, arrows, circles, and valve indicators (top layer - always visible) */}

      {/* Heat exchanger components */}
      <rect
        x="408.992"
        width="89.6791"
        height="330.861"
        rx="41"
        fill="url(#paint0_linear_63304_50040)"
      />
      <rect
        x="482.293"
        y="256.418"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.293 256.418)"
        fill={COLORS.blueShades.darkest}
      />
      <rect
        x="482.074"
        y="218.373"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.074 218.373)"
        fill={COLORS.blueShades.darkest}
      />
      <rect
        x="482.074"
        y="180.327"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.074 180.327)"
        fill={COLORS.blueShades.darkest}
      />
      <rect
        x="482.074"
        y="142.282"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.074 142.282)"
        fill={COLORS.purpleShades2.dark}
      />
      <rect
        x="482.074"
        y="104.235"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.074 104.235)"
        fill={COLORS.purpleShades2.dark}
      />
      <rect
        x="482.074"
        y="66.1904"
        width="58.0394"
        height="10.8702"
        rx="5.4351"
        transform="rotate(150.136 482.074 66.1904)"
        fill={COLORS.redShades.darkest}
      />
      <path
        d="M494.5 23L436.184 54.7444C433.52 56.1945 430.186 55.2518 428.676 52.6216V52.6216C427.174 50.0057 428.022 46.6696 430.591 45.0889L485.998 10.9999L494.5 23Z"
        fill={COLORS.hotWater}
      />
      <path
        d="M425.747 276.959C426.628 274.078 429.667 272.445 432.556 273.302L499 293L496.002 305L429.332 283.795C426.466 282.883 424.867 279.835 425.747 276.959V276.959Z"
        fill={COLORS.coldWater}
      />
      <rect
        x="427.332"
        y="233.709"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.332 233.709)"
        fill={COLORS.blueShades.light}
      />
      <rect
        x="427.113"
        y="195.664"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.113 195.664)"
        fill={COLORS.blueShades.lighter}
      />
      <rect
        x="427.113"
        y="157.618"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.113 157.618)"
        fill={COLORS.purpleShades.blue}
      />
      <rect
        x="427.113"
        y="119.571"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.113 119.571)"
        fill={COLORS.purpleShades.medium}
      />
      <rect
        x="427.113"
        y="81.5259"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.113 81.5259)"
        fill={COLORS.purpleShades.pink}
      />
      <rect
        x="427.113"
        y="43.4805"
        width="58.2584"
        height="10.8702"
        rx="5.4351"
        transform="rotate(17 427.113 43.4805)"
        fill={COLORS.purpleShades.light}
      />

      {/* Circles and valve indicators */}
      <circle cx="106.661" cy="67.9384" r="6.79387" fill={COLORS.dark} />
      <circle cx="106.662" cy="67.9377" r="4.07632" fill={COLORS.white} />
      <circle cx="606.794" cy="16.7939" r="6.79387" fill={COLORS.dark} />
      <circle cx="606.795" cy="16.7931" r="4.07632" fill={COLORS.white} />
      <circle cx="106.665" cy="260.205" r="6.79387" fill={COLORS.dark} />
      <circle cx="106.662" cy="260.205" r="4.07632" fill={COLORS.white} />

      {/* Target symbol (scaled size to 2x) */}
      <g transform="translate(717 17) scale(2) translate(-717 -17)">
        <circle cx="717" cy="17" r="6" fill={COLORS.white} />
        <path
          fill={COLORS.controlPanel}
          stroke={COLORS.controlPanel}
          strokeWidth="0.5"
          d="M722.649 16.344h-1.93a.655.655 0 1 0 0 1.312h1.93a5.69 5.69 0 0 1-4.993 4.993v-1.93a.654.654 0 0 0-.656-.657.654.654 0 0 0-.656.657v1.93a5.69 5.69 0 0 1-4.993-4.993h1.93a.655.655 0 1 0 0-1.312h-1.93a5.69 5.69 0 0 1 4.993-4.993v1.93c0 .364.292.656.656.656a.654.654 0 0 0 .656-.656v-1.93a5.69 5.69 0 0 1 4.993 4.993M710 17a6.999 6.999 0 0 0 11.95 4.95A6.999 6.999 0 0 0 717 10a7 7 0 0 0-7 7"
        />
      </g>

      <defs>
        <linearGradient
          id="paint0_linear_63304_50040"
          x1="453.832"
          y1="0"
          x2="453.832"
          y2="330.861"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={COLORS.hotWater} />
          <stop offset="1" stopColor={COLORS.coldWater} />
        </linearGradient>
      </defs>
    </svg>
  );
};
